import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RoomShareMapModel } from '../../models/api/Call';
import { ActionAuth, ActionUi } from '..';
import { useFunctionalityRoomShareMap } from '../../pages/hooks/functionalityRoomShareMap';
import { useWebSocket } from '../../pages/hooks';
import { logger } from '../../utils';

type GuestContextType = {
  isLoading: boolean;
  roomMapData: RoomShareMapModel | null;
  token: string | null;
  loadTwilioToken: () => Promise<void>;
  socketConnection: WebSocket | null;
};

const GuestContext = createContext<GuestContextType>({
  isLoading: true,
  roomMapData: null,
  token: null,
  loadTwilioToken: async () => {},
  socketConnection: null,
});

export const useGuestContext = () => useContext(GuestContext);

export const GuestContextProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  
  const [isLoading, setIsLoading] = useState(true);
  const [roomMapData, setRoomMapData] = useState<RoomShareMapModel | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const { guid } = useParams<{ guid: string }>();
  const dispatch = useDispatch();

  const { LoadRoomMap } = useFunctionalityRoomShareMap();
  const { webSocketConnection: socketConnection } = useWebSocket();

  const loadTwilioToken = useCallback(async () => {
    logger({ message: 'GuestContext: Re-fetching Twilio token' });

    if (!guid) {
      logger({ message: 'GuestContext: guid is missing, cannot fetch token' });
      return;
    }

    try {
      const payload = await LoadRoomMap(guid);
      
      if (payload?.webToken) {
        setToken(payload.webToken);
        dispatch(ActionAuth.SetVRItoken(payload.webToken));
      }

      if (payload?.chatSid) {
        dispatch(ActionUi.SetChatSid(payload.chatSid));
      }

      setRoomMapData(payload ?? null);
    } catch (err) {
      console.error('GuestContext: Failed to re-fetch room map data:', err);
    }
  }, [guid, LoadRoomMap, dispatch]);


  useEffect(() => {
    const fetchRoomParameters = async () => {
      setIsLoading(true);
      try {
        const payload = await LoadRoomMap(guid);

        if (payload?.webToken) {
          setToken(payload.webToken);
          dispatch(ActionAuth.SetVRItoken(payload.webToken));
        }

        if (payload?.chatSid) {
          dispatch(ActionUi.SetChatSid(payload.chatSid));
        }
 
        setRoomMapData(payload ?? null);
        dispatch(ActionUi.SetGuid(guid));
      } catch (err) {
        console.error('GuestContext: Initial fetch failed:', err);
        setRoomMapData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (guid) {
      fetchRoomParameters();
    }
  }, [guid, LoadRoomMap, dispatch]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      roomMapData,
      token,
      loadTwilioToken,
      socketConnection,
    }),
    [isLoading, roomMapData, token, loadTwilioToken, socketConnection]
  );

  useEffect(() => {
  loadTwilioToken();
}, []);

  return (
    <GuestContext.Provider value={contextValue}>
      {children}
    </GuestContext.Provider>
  );
};
